import React, { useMemo, useState, useCallback } from 'react';
import { FaRobot, FaSync, FaPlay, FaFilePdf } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ITEMS, INVENTORY, ORDERS, VENDORS } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import '../styles/Reports.css';

const FLOWISE_API = 'https://www.windyflo.com/api/v1/prediction/e683b964-b8d2-441b-88af-265a0e9331b3';
const CLAUDE_API_KEY = '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';

// ═══════════════════════════════════════════════
// API 호출
// ═══════════════════════════════════════════════
async function callWindyFlo(prompt) {
  try {
    const res = await fetch(FLOWISE_API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: prompt }),
    });
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    return data.text || data.answer || data.output || data.response || (typeof data === 'string' ? data : JSON.stringify(data));
  } catch (e) { console.error('WindyFlo API:', e); return null; }
}

async function callClaude(prompt, maxTokens = 4096) {
  if (!CLAUDE_API_KEY || CLAUDE_API_KEY === 'YOUR_CLAUDE_API_KEY') return callWindyFlo(prompt);
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': CLAUDE_API_KEY, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
      body: JSON.stringify({ model: 'claude-sonnet-4-20250514', max_tokens: maxTokens, messages: [{ role: 'user', content: prompt }] }),
    });
    if (!res.ok) return callWindyFlo(prompt);
    const data = await res.json();
    return data.content?.find(c => c.type === 'text')?.text || callWindyFlo(prompt);
  } catch (e) { console.error('Claude API:', e); return callWindyFlo(prompt); }
}

// ═══════════════════════════════════════════════
// 시뮬레이션 엔진
// ═══════════════════════════════════════════════
function runSimulation(params) {
  const { demandChange, ltChange, defRateChange, costChange } = params;
  const simInventory = INVENTORY.map(it => {
    const newReserved = Math.round(it.reserved * (1 + demandChange / 100));
    const newDefect = Math.max(0, Math.round(it.defect * (1 + defRateChange / 100)));
    const newCost = Math.round(it.cost * (1 + costChange / 100));
    const newAtp = it.good - newReserved;
    return { ...it, reserved: newReserved, defect: newDefect, atp: newAtp, cost: newCost,
      needOrder: newAtp <= 0, status: newAtp <= 0 ? '발주필요' : newAtp < 100 ? '주의' : '정상', lossAmt: newDefect * newCost };
  });
  const simVendors = VENDORS.map(v => ({ ...v, lt: Math.max(1, Math.round(v.lt * (1 + ltChange / 100))), cost: Math.round(v.cost * (1 + costChange / 100)) }));

  const calc = (inv, vds) => {
    const needOrder = inv.filter(i => i.needOrder).length;
    const totalLoss = inv.reduce((s, i) => s + i.lossAmt, 0);
    const totalVal = inv.reduce((s, i) => s + i.good * i.cost, 0);
    const avgLt = (vds.reduce((s, v) => s + v.lt, 0) / vds.length).toFixed(1);
    const fulfilled = ORDERS.filter(o => { const i = inv.find(x => x.code === o.itemCode); return i ? i.atp >= o.qty : false; }).length;
    const orderCost = inv.filter(i => i.needOrder).reduce((s, it) => {
      const vs = vds.filter(v => v.code === it.code); if (!vs.length) return s;
      const best = vs.sort((a, b) => a.cost - b.cost)[0];
      return s + Math.max(Math.abs(it.atp), best.moq || 100) * best.cost;
    }, 0);
    const byStatus = { '정상': 0, '주의': 0, '발주필요': 0 };
    inv.forEach(i => { byStatus[i.status]++; });
    return { needOrder, totalLoss, totalVal, avgLt, fulfilled, orderCost, byStatus };
  };

  const orig = calc(INVENTORY, VENDORS);
  const sim = calc(simInventory, simVendors);
  const criticalItems = simInventory.filter(i => i.needOrder && !INVENTORY.find(o => o.code === i.code)?.needOrder).sort((a, b) => a.atp - b.atp).slice(0, 10);
  const excessItems = simInventory.filter(i => i.atp > 500 && i.good > i.reserved * 3).sort((a, b) => b.atp - a.atp).slice(0, 10);
  const defectWorse = simInventory.filter(i => { const orig = INVENTORY.find(o => o.code === i.code); return orig && i.lossAmt > orig.lossAmt && i.defect > 0; }).sort((a, b) => b.lossAmt - a.lossAmt).slice(0, 10);
  return { orig, sim, criticalItems, excessItems, defectWorse, simInventory, simVendors };
}

// ═══════════════════════════════════════════════
// AI 분석 카드 (WindyFlo) — i18n 대응
// ═══════════════════════════════════════════════
const AIAnalysisCard = ({ title, dataPrompt, dataSummary, t, fmtCurrency, lang }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const langInstruction = lang === 'ja'
    ? '日本語で回答してください。通貨は¥（円）を使用してください。'
    : '한국어로 답변해주세요.';

  const runAnalysis = useCallback(async () => {
    setLoading(true); setAnalysis(null);
    const prompt = `당신은 제조/유통/물류 기업의 자재관리 ERP 전문 컨설턴트입니다.
아래는 반도체 부품 자재관리 시스템의 실제 데이터입니다.

${dataPrompt}

${langInstruction}
반드시 아래 4가지 섹션을 포함하세요:

## 📋 ${lang === 'ja' ? '重要インサイト' : '핵심 인사이트'}
## ⚠️ ${lang === 'ja' ? 'リスク分析' : '리스크 분석'}
## 💡 ${lang === 'ja' ? '改善提案' : '개선 제안'}
## 📊 ${lang === 'ja' ? '総合評価' : '종합 평가'}`;
    const result = await callWindyFlo(prompt);
    setAnalysis(result || fallbackAnalysis(title, dataSummary, t, fmtCurrency, lang));
    setLoading(false);
  }, [dataPrompt, dataSummary, title, t, fmtCurrency, lang, langInstruction]);

  return (
    <div className="ai-analysis-card">
      <div className="ai-card-header">
        <div className="ai-card-title"><FaRobot className="ai-icon" /><span>{t.rpt.aiAnalysisLabel}: {title}</span></div>
        <button className="ai-run-btn" onClick={runAnalysis} disabled={loading}>
          {loading ? <><FaSync className="ai-spin" /> {t.rpt.aiAnalyzing}</> : <><FaRobot /> {t.rpt.aiAnalysisBtn}</>}
        </button>
      </div>
      {loading && <div className="ai-loading"><div className="ai-loading-bar"><div className="ai-loading-fill" /></div><span>{t.rpt.analyzing}</span></div>}
      {analysis && <div className="ai-result"><ReactMarkdown remarkPlugins={[remarkGfm]}>{analysis}</ReactMarkdown></div>}
      {!analysis && !loading && <div className="ai-placeholder"><FaRobot className="ai-placeholder-icon" /><p>{t.rpt.aiPlaceholder.split('\\n').map((line, i) => <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>)}</p></div>}
    </div>
  );
};

function fallbackAnalysis(title, data, t, fmtCurrency, lang) {
  const n = (v) => v != null ? Number(v).toLocaleString() : '-';
  if (lang === 'ja') {
    return `## 📋 重要インサイト\n- データ分析が完了しました。\n\n## ⚠️ リスク分析\n- 主要リスクを確認中です。\n\n## 💡 改善提案\n1. 緊急発注品目の即時対応\n2. サプライヤー品質管理の強化\n\n## 📊 総合評価\n現在の状態: **⚠️ 注意**`;
  }
  return `## 📋 핵심 인사이트\n- 데이터 분석이 완료되었습니다.\n\n## ⚠️ 리스크 분석\n- 주요 리스크를 확인 중입니다.\n\n## 💡 개선 제안\n1. 긴급 발주 품목 즉시 대응\n2. 공급업체 품질 관리 강화\n\n## 📊 종합 평가\n현재 상태: **⚠️ 주의**`;
}

// ═══════════════════════════════════════════════
// 메인 컴포넌트
// ═══════════════════════════════════════════════
const ReportsPage = () => {
  const { t, lang, fmtCurrency, fmtNum } = useLang();
  const fmt = (n) => n != null ? Number(n).toLocaleString() : '-';
  const pct = (a, b) => b !== 0 ? ((a - b) / b * 100).toFixed(1) : '0';
  const fmtSign = (v) => v > 0 ? `+${v}` : `${v}`;
  const cs = t.rpt.currencySymbol; // ₩ or ¥
  // 통화 표시 (보고서 내 정적 데이터용)
  const fc = (v) => fmtCurrency(v);

  const [activeReport, setActiveReport] = useState('inventory');
  const [simParams, setSimParams] = useState({ demandChange: 0, ltChange: 0, defRateChange: 0, costChange: 0 });
  const [simResult, setSimResult] = useState(null);
  const [simReport, setSimReport] = useState(null);
  const [simReportLoading, setSimReportLoading] = useState(false);

  // 그룹명 번역
  const gl = (g) => {
    if (lang === 'ja') {
      if (g === '메모리') return 'メモリ'; if (g === '비메모리') return '非メモリ'; if (g === 'SiP 외') return 'SiP他';
    }
    return g;
  };
  // 상태 번역
  const sl = (s) => {
    if (s === '정상') return t.rpt.normal; if (s === '주의') return t.rpt.caution;
    return t.rpt.needOrder;
  };

  // 데이터 계산
  const invReport = useMemo(() => {
    const byGroup = {};
    INVENTORY.forEach(it => {
      if (!byGroup[it.group]) byGroup[it.group] = { count: 0, good: 0, reserved: 0, defect: 0, loss: 0, needOrder: 0 };
      const g = byGroup[it.group]; g.count++; g.good += it.good; g.reserved += it.reserved; g.defect += it.defect; g.loss += it.lossAmt; if (it.needOrder) g.needOrder++;
    });
    const byStatus = { '정상': 0, '주의': 0, '발주필요': 0 };
    INVENTORY.forEach(it => { byStatus[it.status]++; });
    return { byGroup, byStatus, totalGood: INVENTORY.reduce((s, i) => s + i.good, 0), totalRes: INVENTORY.reduce((s, i) => s + i.reserved, 0), totalDef: INVENTORY.reduce((s, i) => s + i.defect, 0), totalVal: INVENTORY.reduce((s, i) => s + i.good * i.cost, 0), totalLoss: INVENTORY.reduce((s, i) => s + i.lossAmt, 0) };
  }, []);

  const vendorReport = useMemo(() => {
    const map = {};
    VENDORS.forEach(v => { if (!map[v.vName]) map[v.vName] = { items: [], totalCost: 0, totalLt: 0, totalDef: 0, count: 0 }; const m = map[v.vName]; m.items.push(v); m.totalCost += v.cost; m.totalLt += v.lt; m.totalDef += v.defRate; m.count++; });
    return Object.entries(map).map(([name, d]) => ({ name, count: d.count, avgCost: Math.round(d.totalCost / d.count), avgLt: (d.totalLt / d.count).toFixed(1), avgDefRate: ((d.totalDef / d.count) * 100).toFixed(2) })).sort((a, b) => parseFloat(a.avgDefRate) - parseFloat(b.avgDefRate));
  }, []);

  const orderReport = useMemo(() => {
    const analysis = ORDERS.map(o => {
      const item = ITEMS.find(i => i.code === o.itemCode); const inv = INVENTORY.find(i => i.code === o.itemCode);
      const atp = inv ? inv.atp : 0; const shortage = Math.max(0, o.qty - atp);
      const vs = VENDORS.filter(v => v.code === o.itemCode);
      let bestVendor = null, orderQty = 0, orderCost = 0;
      if (shortage > 0 && vs.length > 0) { const sorted = o.rule === 'Cost' ? [...vs].sort((a, b) => a.cost - b.cost) : [...vs].sort((a, b) => a.lt - b.lt); bestVendor = sorted[0]; orderQty = Math.max(shortage, bestVendor.moq); orderQty = Math.ceil(orderQty / bestVendor.lot) * bestVendor.lot; orderCost = orderQty * bestVendor.cost; }
      return { ...o, atp, shortage, fulfilled: shortage === 0, bestVendor, orderQty, orderCost };
    });
    return { analysis, totalOrderCost: analysis.reduce((s, a) => s + a.orderCost, 0), fulfilled: analysis.filter(a => a.fulfilled).length, unfulfilled: analysis.filter(a => !a.fulfilled).length };
  }, []);

  const defectReport = useMemo(() => {
    const items = INVENTORY.filter(i => i.defect > 0).sort((a, b) => b.lossAmt - a.lossAmt);
    const totalLoss = items.reduce((s, i) => s + i.lossAmt, 0);
    const byGroup = {};
    items.forEach(it => { if (!byGroup[it.group]) byGroup[it.group] = { count: 0, defect: 0, loss: 0 }; byGroup[it.group].count++; byGroup[it.group].defect += it.defect; byGroup[it.group].loss += it.lossAmt; });
    return { items, totalLoss, byGroup };
  }, []);

  // ═══ 시뮬레이션 보고서 생성 ═══
  const handleGenerateReport = useCallback(async () => {
    const result = runSimulation(simParams);
    setSimResult(result); setSimReportLoading(true); setSimReport(null);
    const { orig, sim, criticalItems, excessItems, defectWorse } = result;

    const dataContext = `
[현재 재고 현황 / Current Inventory]
- 총 품목: ${ITEMS.length}개
- 재고 상태: 정상 ${orig.byStatus['정상']}개 / 주의 ${orig.byStatus['주의']}개 / 발주필요 ${orig.byStatus['발주필요']}개
- 재고 자산가치: ₩${fmt(orig.totalVal)}
- 불량 손실액: ₩${fmt(orig.totalLoss)}
- 주문 충족률: ${orig.fulfilled}/${ORDERS.length}건 (${(orig.fulfilled/ORDERS.length*100).toFixed(0)}%)
- 예상 발주비용: ₩${fmt(orig.orderCost)}
- 평균 리드타임: ${orig.avgLt}일

[시뮬레이션 조건 / Simulation Parameters]
- 수요 변동: ${fmtSign(simParams.demandChange)}%
- 리드타임 변동: ${fmtSign(simParams.ltChange)}%
- 불량율 변동: ${fmtSign(simParams.defRateChange)}%
- 구매 단가 변동: ${fmtSign(simParams.costChange)}%

[시뮬레이션 결과 / Simulation Results]
- 재고 상태: 정상 ${sim.byStatus['정상']}개 / 주의 ${sim.byStatus['주의']}개 / 발주필요 ${sim.byStatus['발주필요']}개
- 재고 자산가치: ₩${fmt(sim.totalVal)} (${pct(sim.totalVal, orig.totalVal)}%)
- 불량 손실액: ₩${fmt(sim.totalLoss)} (${pct(sim.totalLoss, orig.totalLoss)}%)
- 주문 충족률: ${sim.fulfilled}/${ORDERS.length}건 (${(sim.fulfilled/ORDERS.length*100).toFixed(0)}%)
- 예상 발주비용: ₩${fmt(sim.orderCost)} (${pct(sim.orderCost, orig.orderCost)}%)
- 평균 리드타임: ${sim.avgLt}일

[신규 발주필요 품목: ${criticalItems.length}건]
${criticalItems.map(it => `- ${it.code}: ATP ${fmt(INVENTORY.find(o => o.code === it.code)?.atp)} → ${fmt(it.atp)}`).join('\n')}

[과잉재고 위험: ${excessItems.length}건]
${excessItems.map(it => `- ${it.code}: ATP ${fmt(it.atp)}, Good ${fmt(it.good)}, Reserved ${fmt(it.reserved)}, 자산 ₩${fmt(it.good * it.cost)}`).join('\n')}

[불량 악화 품목: ${defectWorse.length}건]
${defectWorse.map(it => { const o = INVENTORY.find(x => x.code === it.code); return `- ${it.code}: 불량 ${fmt(o?.defect)} → ${fmt(it.defect)}, 손실 ₩${fmt(o?.lossAmt)} → ₩${fmt(it.lossAmt)}`; }).join('\n')}

[공급업체 현황]
${vendorReport.map(v => `- ${v.name}: ${v.count}건, 단가 ₩${fmt(v.avgCost)}, LT ${v.avgLt}일, 불량율 ${v.avgDefRate}%`).join('\n')}
`;

    const today = new Date();
    let prompt;

    if (lang === 'ja') {
      const dateStr = `${today.getFullYear()}年${today.getMonth()+1}月${today.getDate()}日`;
      prompt = `あなたは製造・流通・物流企業の資材管理ERPコンサルタントです。
以下の半導体部品資材管理システムのデータに基づき、代表取締役が一目で把握できる**経営者向けシミュレーション分析レポート**を日本語で作成してください。

重要：
- 通貨はすべて¥（円）で表示してください。元データの₩（ウォン）の金額は、0.11を掛けて円に換算してください。
- レポート作成日: ${dateStr}
- 担当: 資材管理 ERP AI
- レポート末尾に「レポート作成日」などの文言を絶対に追加しないでください。

${dataContext}

以下の形式のマークダウンレポートを作成してください。すべてのセクションを必ず含めてください：

# 📊 在庫シミュレーション分析レポート

> レポート作成日: ${dateStr} | 担当: 資材管理 ERP AI

## 1. Executive Summary
> 経営者が30秒で把握できるよう、核心3～4行をblockquoteで記述。最重要変化、リスク等級(🟢/🟡/🔴)、即時アクション。

## 2. シナリオ概要
| 変動要素 | 設定値 | 意味 |

## 3. 主要指標変化分析
| 指標 | Before | After | 変動率 | 影響 |
> アドバイス

## 4. 滞留在庫・過剰在庫分析
> アドバイス

## 5. 不良率・品質リスク
> アドバイス

## 6. サプライチェーン安定性評価

## 7. 財務影響シミュレーション
| 項目 | 金額 | 前期比 | 影響度 |

## 8. 実行ロードマップ
| 時期 | アクション | 担当 | 予想効果 |
|---|---|---|---|
| 即時 | ... | ... | ... |
| 1週間内 | ... | ... | ... |
| 1ヶ月内 | ... | ... | ... |
| 四半期 | ... | ... | ... |

## 9. 総合意見
> **リスク等級: [🟢安全 / 🟡注意 / 🔴危険]**

作成ルール：
1. blockquote("> ")を積極活用
2. 数値比較は表で整理
3. 絵文字(⬆️⬇️➡️🟢🟡🔴)を積極使用
4. 各セクション末尾にblockquoteでアドバイス
5. すべての金額は¥（円）で表示`;
    } else {
      const dateStr = `${today.getFullYear()}년 ${today.getMonth()+1}월 ${today.getDate()}일`;
      prompt = `당신은 제조/유통/물류 기업의 자재관리 ERP 전문 컨설턴트입니다.
아래 반도체 부품 자재관리 시스템의 실제 데이터를 기반으로, 대표이사가 한눈에 파악할 수 있는 **경영진용 시뮬레이션 분석 보고서**를 작성해주세요.

☝️ 필수 규칙:
- 보고서 작성일: ${dateStr}
- 담당: 자재관리 ERP AI
- "보고서 작성일" 등의 문구를 보고서 하단에 절대 추가하지 마세요.
- 보고서 끝에 "보고서 작성일: xxxx년 기준 / 담당: xxx" 같은 문구를 절대 추가하지 마세요.

${dataContext}

아래 형식의 마크다운 보고서를 한국어로 작성해주세요. 모든 섹션을 반드시 포함하세요:

# 📊 재고 시뮬레이션 분석 보고서

> 보고서 작성일: ${dateStr} | 담당: 자재관리 ERP AI

## 1. Executive Summary
> 핵심 3~4줄 blockquote. 가장 중요한 변화, 위험 등급(🟢/🟡/🔴), 즉시 필요한 액션.

## 2. 시나리오 개요
| 변동 요소 | 설정값 | 의미 |

## 3. 핵심 지표 변화 분석
| 지표 | Before | After | 변동률 | 영향 |
> 핵심 조언

## 4. 악성재고 & 과잉재고 분석
> 조언

## 5. 불량률 & 품질 리스크
> 조언

## 6. 공급망 안정성 평가

## 7. 재무 영향 시뮬레이션
| 항목 | 금액 | 전기 대비 | 영향도 |

## 8. 실행 로드맵
| 시기 | 액션 | 담당 | 예상 효과 |
|---|---|---|---|
| 즉시 | ... | ... | ... |
| 1주 내 | ... | ... | ... |
| 1개월 내 | ... | ... | ... |
| 분기 | ... | ... | ... |

## 9. 종합 의견
> **위험 등급: [🟢안전 / 🟡주의 / 🔴위험 중 하나]**

중요 작성 규칙:
1. **blockquote("> ")를 적극 활용**
2. **모든 수치 비교는 표(테이블)로**
3. **이모지(⬆️⬇️➡️🟢🟡🔴)를 적극 사용**
4. 각 섹션 마지막에 **blockquote로 조언/권고**
5. 보고서 작성일/담당은 제목 아래 blockquote에만
6. 모든 수치는 구체적으로 포함`;
    }

    let result2 = await callClaude(prompt, 4096);
    if (result2) {
      result2 = result2.replace(/\n+[-*_]{3,}\s*\n*\s*(보고서 ?작성일|レポート作成日)[^\n]*/gi, '');
      result2 = result2.replace(/\n+(보고서 ?작성일|レポート作成日)[:\s]*[^\n]*/gi, '');
      result2 = result2.replace(/\n+\*?(작성일|担当)[:\s]*[^\n]*$/gi, '');
      result2 = result2.trim();
    }
    setSimReport(result2 || generateLocalReport(simParams, result));
    setSimReportLoading(false);
  }, [simParams, vendorReport, lang]);

  // 로컬 폴백 보고서
  function generateLocalReport(params, result) {
    const { orig, sim, criticalItems, excessItems, defectWorse } = result;
    const needOrderDelta = sim.needOrder - orig.needOrder;
    const lossDelta = sim.totalLoss - orig.totalLoss;
    const costDelta = sim.orderCost - orig.orderCost;
    const riskLevel = sim.needOrder > orig.needOrder * 1.3 || sim.fulfilled < orig.fulfilled
      ? (lang === 'ja' ? '🔴 危険' : '🔴 위험')
      : sim.needOrder > orig.needOrder
        ? (lang === 'ja' ? '🟡 注意' : '🟡 주의')
        : (lang === 'ja' ? '🟢 安全' : '🟢 안전');

    if (lang === 'ja') {
      const cv = (v) => `¥${fmt(Math.round(v * 0.11))}`;
      return `# 📊 在庫シミュレーション分析レポート

## 1. Executive Summary
シミュレーション条件下で要発注品目が${orig.needOrder}件→${sim.needOrder}件に変動。受注充足率は${(orig.fulfilled/ORDERS.length*100).toFixed(0)}%→${(sim.fulfilled/ORDERS.length*100).toFixed(0)}%。

> **リスク等級: ${riskLevel}**

## 2. シナリオ概要
| 変動要素 | 設定値 | 意味 |
|---|---|---|
| 需要変動 | ${fmtSign(params.demandChange)}% | 顧客注文量${params.demandChange > 0 ? '増加' : params.demandChange < 0 ? '減少' : '維持'} |
| リードタイム変動 | ${fmtSign(params.ltChange)}% | サプライヤー納期${params.ltChange > 0 ? '遅延' : params.ltChange < 0 ? '短縮' : '維持'} |
| 不良率変動 | ${fmtSign(params.defRateChange)}% | 入荷品質${params.defRateChange > 0 ? '悪化' : params.defRateChange < 0 ? '改善' : '維持'} |
| 単価変動 | ${fmtSign(params.costChange)}% | 原材料価格${params.costChange > 0 ? '上昇' : params.costChange < 0 ? '下落' : '維持'} |

## 3. 主要指標変化
| 指標 | Before | After | 変動 | 影響 |
|---|---|---|---|---|
| 要発注品目 | ${orig.needOrder}件 | ${sim.needOrder}件 | ${fmtSign(needOrderDelta)}件 | ${needOrderDelta > 0 ? '⬆️ 緊急発注増加' : '➡️ 安定'} |
| 不良損失額 | ${cv(orig.totalLoss)} | ${cv(sim.totalLoss)} | ${pct(sim.totalLoss, orig.totalLoss)}% | ${lossDelta > 0 ? '⬆️ 品質管理必要' : '⬇️ 改善'} |
| 在庫資産価値 | ${cv(orig.totalVal)} | ${cv(sim.totalVal)} | ${pct(sim.totalVal, orig.totalVal)}% | キャッシュフロー影響 |
| 受注充足率 | ${orig.fulfilled}/${ORDERS.length}件 | ${sim.fulfilled}/${ORDERS.length}件 | ${sim.fulfilled - orig.fulfilled}件 | ${sim.fulfilled < orig.fulfilled ? '⬆️ 売上リスク' : '安定'} |

## 8. 実行ロードマップ
| 時期 | アクション | 担当 | 予想効果 |
|---|---|---|---|
| 即時 | 要発注${sim.needOrder}件の緊急発注 | 購買チーム | 受注充足率確保 |
| 1週間 | 不良上位品目の入荷検査強化 | 品質チーム | 損失額20%↓ |
| 1ヶ月 | サプライヤー実績レビュー | 購買チーム | 不良率改善 |
| 四半期 | 安全在庫水準の再設定 | 経営企画 | 長期安定化 |

## 9. 総合意見
> **リスク等級: ${riskLevel}**`;
    }

    return `# 📊 재고 시뮬레이션 분석 보고서

## 1. Executive Summary
시뮬레이션 조건에서 발주필요 품목이 ${orig.needOrder}건→${sim.needOrder}건으로 변동. 주문 충족률 ${(orig.fulfilled/ORDERS.length*100).toFixed(0)}%→${(sim.fulfilled/ORDERS.length*100).toFixed(0)}%.

> **위험 등급: ${riskLevel}**

## 2. 시나리오 개요
| 변동 요소 | 설정값 | 의미 |
|---|---|---|
| 수요 변동 | ${fmtSign(params.demandChange)}% | 고객 주문량 ${params.demandChange > 0 ? '증가' : params.demandChange < 0 ? '감소' : '유지'} |
| 리드타임 변동 | ${fmtSign(params.ltChange)}% | 공급업체 납기 ${params.ltChange > 0 ? '지연' : params.ltChange < 0 ? '단축' : '유지'} |
| 불량율 변동 | ${fmtSign(params.defRateChange)}% | 입고 품질 ${params.defRateChange > 0 ? '악화' : params.defRateChange < 0 ? '개선' : '유지'} |
| 단가 변동 | ${fmtSign(params.costChange)}% | 원자재 가격 ${params.costChange > 0 ? '상승' : params.costChange < 0 ? '하락' : '유지'} |

## 3. 핵심 지표 변화
| 지표 | Before | After | 변동 | 영향 |
|---|---|---|---|---|
| 발주필요 품목 | ${orig.needOrder}건 | ${sim.needOrder}건 | ${fmtSign(needOrderDelta)}건 | ${needOrderDelta > 0 ? '⬆️ 긴급 발주 증가' : '➡️ 안정'} |
| 불량 손실액 | ₩${fmt(orig.totalLoss)} | ₩${fmt(sim.totalLoss)} | ${pct(sim.totalLoss, orig.totalLoss)}% | ${lossDelta > 0 ? '⬆️ 품질 관리 필요' : '⬇️ 개선'} |
| 재고 자산가치 | ₩${fmt(orig.totalVal)} | ₩${fmt(sim.totalVal)} | ${pct(sim.totalVal, orig.totalVal)}% | 현금흐름 영향 |
| 주문 충족률 | ${orig.fulfilled}/${ORDERS.length}건 | ${sim.fulfilled}/${ORDERS.length}건 | ${sim.fulfilled - orig.fulfilled}건 | ${sim.fulfilled < orig.fulfilled ? '⬆️ 매출 리스크' : '안정'} |

## 8. 실행 로드맵
| 시기 | 액션 | 담당 | 예상 효과 |
|---|---|---|---|
| 즉시 | 발주필요 ${sim.needOrder}건 긴급 발주 | 구매팀 | 주문 충족률 확보 |
| 1주 | 불량 상위 품목 입고검수 강화 | 품질팀 | 손실액 20%↓ |
| 1개월 | 공급업체 성과 리뷰 | 구매팀 | 불량율 개선 |
| 분기 | 안전재고 수준 재설정 | 경영기획 | 장기 안정화 |

## 9. 종합 의견
> **위험 등급: ${riskLevel}**`;
  }

  // 프리셋
  const presets = [
    { label: t.rpt.presetDemandSurge, params: { demandChange: 30, ltChange: 0, defRateChange: 0, costChange: 0 } },
    { label: t.rpt.presetSupplyDelay, params: { demandChange: 10, ltChange: 50, defRateChange: 0, costChange: 5 } },
    { label: t.rpt.presetQualityDown, params: { demandChange: 0, ltChange: 0, defRateChange: 80, costChange: 0 } },
    { label: t.rpt.presetInflation, params: { demandChange: 0, ltChange: 20, defRateChange: 10, costChange: 15 } },
    { label: t.rpt.presetWorstCase, params: { demandChange: 50, ltChange: 40, defRateChange: 50, costChange: 20 } },
    { label: t.rpt.presetImprove, params: { demandChange: 0, ltChange: -20, defRateChange: -50, costChange: -5 } },
  ];

  const reports = [
    { id: 'inventory', name: t.reports.inventory, icon: '📊', desc: t.reports.inventoryDesc },
    { id: 'vendor', name: t.reports.vendor, icon: '🤝', desc: t.reports.vendorDesc },
    { id: 'order', name: t.reports.order, icon: '🛒', desc: t.reports.orderDesc },
    { id: 'defect', name: t.reports.defect, icon: '📉', desc: t.reports.defectDesc },
    { id: 'simulation', name: t.reports.simulation, icon: '🧪', desc: t.reports.simulationDesc },
  ];

  const dateLocale = lang === 'ja' ? 'ja-JP' : 'ko-KR';

  return (
    <div className="reports-page">
      <div className="reports-tabs">
        {reports.map(r => (
          <button key={r.id} className={`report-tab ${activeReport === r.id ? 'active' : ''}`} onClick={() => { setActiveReport(r.id); setSimReport(null); setSimResult(null); }}>
            <span className="report-tab-icon">{r.icon}</span>
            <div className="report-tab-info"><span className="report-tab-name">{r.name}</span><span className="report-tab-desc">{r.desc}</span></div>
          </button>
        ))}
      </div>

      <div className="report-actions">
        <span className="report-date">📅 {new Date().toLocaleDateString(dateLocale)} {new Date().toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })}</span>
        <button className="report-print-btn" onClick={() => window.print()}>{t.rpt.printPdf}</button>
      </div>

      {/* ═══ 재고 종합 분석 ═══ */}
      {activeReport === 'inventory' && (
        <div className="report-content">
          <h2 className="report-title">{t.rpt.invTitle}</h2>
          <div className="report-kpi-row">
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.totalItems}</span><span className="rkpi-val blue">{ITEMS.length}{t.rpt.items}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.goodInv}</span><span className="rkpi-val green">{fmt(invReport.totalGood)} EA</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.reserved}</span><span className="rkpi-val orange">{fmt(invReport.totalRes)} EA</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.defect}</span><span className="rkpi-val red">{fmt(invReport.totalDef)} EA</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.assetValue}</span><span className="rkpi-val purple">{fc(invReport.totalVal)}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.defLoss}</span><span className="rkpi-val red">{fc(invReport.totalLoss)}</span></div>
          </div>
          <h3 className="report-section-title">{t.rpt.groupInv}</h3>
          <table className="report-table"><thead><tr><th>{t.rpt.group}</th><th>{t.rpt.count}</th><th>Good</th><th>Reserved</th><th>Defect</th><th>{t.rpt.needOrder}</th><th>{t.rpt.defLoss}</th></tr></thead>
            <tbody>{Object.entries(invReport.byGroup).map(([group, d]) => (
              <tr key={group}><td><strong>{gl(group)}</strong></td><td>{d.count}{t.rpt.items}</td><td>{fmt(d.good)}</td><td>{fmt(d.reserved)}</td>
                <td style={{ color: d.defect > 0 ? '#dc2626' : undefined }}>{fmt(d.defect)}</td>
                <td style={{ color: d.needOrder > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{d.needOrder}{t.rpt.cases}</td>
                <td style={{ color: '#dc2626', fontWeight: 600 }}>{fc(d.loss)}</td></tr>
            ))}</tbody></table>
          <h3 className="report-section-title">{t.rpt.statusDist}</h3>
          <div className="report-status-bars">{Object.entries(invReport.byStatus).map(([status, count]) => {
            const p = Math.round(count / INVENTORY.length * 100); const color = status === '정상' ? '#10b981' : status === '주의' ? '#f59e0b' : '#ef4444';
            return (<div key={status} className="report-bar-item"><span className="report-bar-label">{sl(status)}</span><div className="report-bar-track"><div className="report-bar-fill" style={{ width: `${p}%`, background: color }} /></div><span className="report-bar-val">{count}{t.rpt.items} ({p}%)</span></div>);
          })}</div>
          <h3 className="report-section-title">{t.rpt.needOrderItems}</h3>
          <table className="report-table"><thead><tr><th>#</th><th>{t.rpt.code}</th><th>{t.rpt.name}</th><th>Good</th><th>Reserved</th><th>ATP</th></tr></thead>
            <tbody>{INVENTORY.filter(i => i.needOrder).sort((a, b) => a.atp - b.atp).map((it, i) => (
              <tr key={it.code + i}><td>{i + 1}</td><td><strong>{it.code}</strong></td><td>{it.name.split(' ; ')[1] || it.name}</td><td>{fmt(it.good)}</td><td>{fmt(it.reserved)}</td><td style={{ color: '#dc2626', fontWeight: 700 }}>{fmt(it.atp)}</td></tr>
            ))}</tbody></table>
          <AIAnalysisCard title={t.reports.inventory} dataPrompt={`[재고/在庫]\n총 ${ITEMS.length}개, Good ${fmt(invReport.totalGood)}, Defect ${fmt(invReport.totalDef)}\n자산 ₩${fmt(invReport.totalVal)}, 손실 ₩${fmt(invReport.totalLoss)}, 발주필요 ${INVENTORY.filter(i => i.needOrder).length}건`}
            dataSummary={{ totalItems: ITEMS.length, needOrder: INVENTORY.filter(i => i.needOrder).length, totalLoss: invReport.totalLoss, totalVal: invReport.totalVal }} t={t} fmtCurrency={fc} lang={lang} />
        </div>
      )}

      {/* ═══ 공급업체 성과 ═══ */}
      {activeReport === 'vendor' && (
        <div className="report-content">
          <h2 className="report-title">{t.rpt.vendorTitle}</h2>
          <table className="report-table"><thead><tr><th>{t.rpt.vendor}</th><th>{t.rpt.tradeCount}</th><th>{t.rpt.avgCost}</th><th>{t.rpt.avgLt}</th><th>{t.rpt.avgDefRate}</th><th>{t.rpt.grade}</th></tr></thead>
            <tbody>{vendorReport.map(v => {
              const g = parseFloat(v.avgDefRate) < 1.0 ? 'A' : parseFloat(v.avgDefRate) < 2.0 ? 'B' : 'C';
              return (<tr key={v.name}><td><strong>{v.name}</strong></td><td>{v.count}{t.rpt.cases}</td><td>{fc(v.avgCost)}</td>
                <td style={{ color: parseFloat(v.avgLt) > 7 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{v.avgLt}{t.rpt.day}</td>
                <td style={{ color: parseFloat(v.avgDefRate) > 2 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{v.avgDefRate}%</td>
                <td><span className={`rt-badge rt-badge-${g === 'A' ? 'green' : g === 'B' ? 'blue' : 'red'}`}>{g}{t.rpt.grade}</span></td></tr>);
            })}</tbody></table>
          {vendorReport.map(v => (
            <div key={v.name} style={{ marginTop: 20 }}>
              <h3 className="report-section-title">{v.name} — {t.rpt.tradeDetail}</h3>
              <table className="report-table"><thead><tr><th>{t.rpt.code}</th><th>{t.rpt.name}</th><th>MOQ</th><th>{t.rpt.unit}</th><th>{lang === 'ja' ? 'リードタイム' : '리드타임'}</th><th>{lang === 'ja' ? '不良率' : '불량율'}</th></tr></thead>
                <tbody>{VENDORS.filter(vv => vv.vName === v.name).map((vv, i) => (
                  <tr key={vv.code + i}><td>{vv.code}</td><td>{vv.name.split(' ; ')[1] || vv.name}</td><td>{fmt(vv.moq)}</td><td>{fc(vv.cost)}</td>
                    <td style={{ color: vv.lt >= 10 ? '#dc2626' : undefined }}>{vv.lt}{t.rpt.day}</td><td style={{ color: vv.defRate >= 0.025 ? '#dc2626' : '#16a34a' }}>{(vv.defRate * 100).toFixed(1)}%</td></tr>
                ))}</tbody></table></div>
          ))}
          <AIAnalysisCard title={t.reports.vendor} dataPrompt={`[공급업체/サプライヤー]\n${vendorReport.map(v => `${v.name}: ${v.count}건, ₩${fmt(v.avgCost)}, ${v.avgLt}일, ${v.avgDefRate}%`).join('\n')}`}
            dataSummary={{ vendorCount: vendorReport.length }} t={t} fmtCurrency={fc} lang={lang} />
        </div>
      )}

      {/* ═══ 주문 충족 ═══ */}
      {activeReport === 'order' && (
        <div className="report-content">
          <h2 className="report-title">{t.rpt.orderTitle}</h2>
          <div className="report-kpi-row">
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.totalOrder}</span><span className="rkpi-val blue">{ORDERS.length}{t.rpt.cases}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.fulfilled}</span><span className="rkpi-val green">{orderReport.fulfilled}{t.rpt.cases}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.unfulfilled}</span><span className="rkpi-val red">{orderReport.unfulfilled}{t.rpt.cases}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.estOrder}</span><span className="rkpi-val purple">{fc(orderReport.totalOrderCost)}</span></div>
          </div>
          <h3 className="report-section-title">{t.rpt.orderAnalysis}</h3>
          <table className="report-table"><thead><tr><th>{lang === 'ja' ? '受注' : '주문'}</th><th>{t.rpt.customer}</th><th>{t.rpt.code}</th><th>{t.rpt.qty}</th><th>ATP</th><th>{t.rpt.shortage}</th><th>{t.rpt.rule}</th><th>{t.rpt.fulfilled}</th></tr></thead>
            <tbody>{orderReport.analysis.map(o => (
              <tr key={o.id}><td><strong>{o.id}</strong></td><td>{o.customer}</td><td>{o.itemCode}</td><td style={{ textAlign: 'right' }}>{fmt(o.qty)}</td>
                <td style={{ textAlign: 'right', color: o.atp < o.qty ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{fmt(o.atp)}</td>
                <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: o.shortage > 0 ? 700 : 400 }}>{o.shortage > 0 ? fmt(o.shortage) : '-'}</td>
                <td><span className={`rt-badge rt-badge-${o.rule === 'Cost' ? 'blue' : 'green'}`}>{o.rule}</span></td>
                <td><span className={`rt-badge rt-badge-${o.fulfilled ? 'green' : 'red'}`}>{o.fulfilled ? '✅' : '❌'}</span></td></tr>
            ))}</tbody></table>
          {orderReport.analysis.filter(o => !o.fulfilled && o.bestVendor).length > 0 && (
            <><h3 className="report-section-title">{t.rpt.autoPoTitle}</h3>
              <table className="report-table"><thead><tr><th>{lang === 'ja' ? '受注' : '주문'}</th><th>{t.rpt.code}</th><th>{t.rpt.shortage}</th><th>{t.rpt.recommended}</th><th>{t.rpt.orderQty}</th><th>{t.rpt.unit}</th><th>{t.rpt.amount}</th><th>{t.rpt.delivery}</th></tr></thead>
                <tbody>{orderReport.analysis.filter(o => !o.fulfilled && o.bestVendor).map(o => (
                  <tr key={o.id}><td><strong>{o.id}</strong></td><td>{o.itemCode}</td><td style={{ color: '#dc2626', fontWeight: 600 }}>{fmt(o.shortage)}</td><td><strong>{o.bestVendor.vName}</strong></td>
                    <td>{fmt(o.orderQty)}</td><td>{fc(o.bestVendor.cost)}</td><td style={{ fontWeight: 700 }}>{fc(o.orderCost)}</td><td>{o.bestVendor.lt}{t.rpt.day}</td></tr>
                ))}</tbody>
                <tfoot><tr style={{ fontWeight: 700, background: '#f8fafc' }}><td colSpan={6} style={{ textAlign: 'right' }}>{t.rpt.total}</td><td>{fc(orderReport.totalOrderCost)}</td><td></td></tr></tfoot></table></>
          )}
          <AIAnalysisCard title={t.reports.order} dataPrompt={`[주문/受注] ${ORDERS.length}건, 충족 ${orderReport.fulfilled}, 미충족 ${orderReport.unfulfilled}, 발주 ₩${fmt(orderReport.totalOrderCost)}`}
            dataSummary={{ totalOrders: ORDERS.length, unfulfilled: orderReport.unfulfilled, totalOrderCost: orderReport.totalOrderCost }} t={t} fmtCurrency={fc} lang={lang} />
        </div>
      )}

      {/* ═══ 불량/손실 ═══ */}
      {activeReport === 'defect' && (
        <div className="report-content">
          <h2 className="report-title">{t.rpt.defTitle}</h2>
          <div className="report-kpi-row">
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.defItems}</span><span className="rkpi-val orange">{defectReport.items.length}{t.rpt.items}</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.totalDef}</span><span className="rkpi-val red">{fmt(defectReport.items.reduce((s, i) => s + i.defect, 0))} EA</span></div>
            <div className="report-kpi"><span className="rkpi-label">{t.rpt.totalLoss}</span><span className="rkpi-val red">{fc(defectReport.totalLoss)}</span></div>
          </div>
          <h3 className="report-section-title">{t.rpt.groupDef}</h3>
          <table className="report-table"><thead><tr><th>{t.rpt.group}</th><th>{lang === 'ja' ? '品目' : '품목'}</th><th>{lang === 'ja' ? '不良' : '불량'}</th><th>{lang === 'ja' ? '損失' : '손실'}</th><th>{t.rpt.ratio}</th></tr></thead>
            <tbody>{Object.entries(defectReport.byGroup).map(([g, d]) => (
              <tr key={g}><td><strong>{gl(g)}</strong></td><td>{d.count}{t.rpt.items}</td><td>{fmt(d.defect)} EA</td><td style={{ color: '#dc2626', fontWeight: 600 }}>{fc(d.loss)}</td><td>{(d.loss / defectReport.totalLoss * 100).toFixed(1)}%</td></tr>
            ))}</tbody></table>
          <h3 className="report-section-title">{t.rpt.defDetail}</h3>
          <table className="report-table"><thead><tr><th>#</th><th>{t.rpt.code}</th><th>{t.rpt.name}</th><th>{t.rpt.group}</th><th>{lang === 'ja' ? '不良' : '불량'}</th><th>{t.rpt.unit}</th><th>{lang === 'ja' ? '損失' : '손실'}</th><th>{t.rpt.ratio}</th></tr></thead>
            <tbody>{defectReport.items.map((it, i) => (
              <tr key={it.code + i}><td>{i + 1}</td><td><strong>{it.code}</strong></td><td>{it.name.split(' ; ')[1] || it.name}</td><td>{gl(it.group)}</td><td>{fmt(it.defect)}</td><td>{fc(it.cost)}</td>
                <td style={{ color: '#dc2626', fontWeight: 700 }}>{fc(it.lossAmt)}</td><td>{(it.lossAmt / defectReport.totalLoss * 100).toFixed(1)}%</td></tr>
            ))}</tbody></table>
          <AIAnalysisCard title={t.reports.defect} dataPrompt={`[불량/不良] ${defectReport.items.length}개, 손실 ₩${fmt(defectReport.totalLoss)}\n상위: ${defectReport.items.slice(0, 5).map(i => `${i.code}:₩${fmt(i.lossAmt)}`).join(', ')}`}
            dataSummary={{ totalLoss: defectReport.totalLoss }} t={t} fmtCurrency={fc} lang={lang} />
        </div>
      )}

      {/* ═══════════════ 시뮬레이션 보고서 ═══════════════ */}
      {activeReport === 'simulation' && (
        <div className="report-content">
          <h2 className="report-title">{t.rpt.simTitle}</h2>
          <p className="sim-desc">{t.rpt.simDesc}</p>

          <div className="sim-presets">
            {presets.map((p, i) => (
              <button key={i} className={`sim-preset-btn ${JSON.stringify(simParams) === JSON.stringify(p.params) ? 'sim-preset-active' : ''}`}
                onClick={() => { setSimParams(p.params); setSimResult(null); setSimReport(null); }}>{p.label}</button>
            ))}
          </div>

          <div className="sim-params">
            {[
              { key: 'demandChange', label: t.rpt.demandLabel, unit: '%', min: -50, max: 100, color: '#3b82f6' },
              { key: 'ltChange', label: t.rpt.ltLabel, unit: '%', min: -50, max: 100, color: '#f59e0b' },
              { key: 'defRateChange', label: t.rpt.defRateLabel, unit: '%', min: -80, max: 200, color: '#ef4444' },
              { key: 'costChange', label: t.rpt.costLabel, unit: '%', min: -30, max: 50, color: '#8b5cf6' },
            ].map(p => (
              <div key={p.key} className="sim-param">
                <div className="sim-param-header">
                  <span className="sim-param-label">{p.label}</span>
                  <span className="sim-param-value" style={{ color: simParams[p.key] > 0 ? '#dc2626' : simParams[p.key] < 0 ? '#16a34a' : '#64748b' }}>
                    {simParams[p.key] > 0 ? '+' : ''}{simParams[p.key]}{p.unit}
                  </span>
                </div>
                <input type="range" min={p.min} max={p.max} value={simParams[p.key]} className="sim-slider" style={{ accentColor: p.color }}
                  onChange={(e) => { setSimParams(prev => ({ ...prev, [p.key]: Number(e.target.value) })); setSimResult(null); setSimReport(null); }} />
                <div className="sim-param-range"><span>{p.min}{p.unit}</span><span>0</span><span>+{p.max}{p.unit}</span></div>
              </div>
            ))}
          </div>

          <button className="sim-run-btn" onClick={handleGenerateReport} disabled={simReportLoading}>
            {simReportLoading ? <><FaSync className="ai-spin" /> {t.rpt.generating}</> : <><FaFilePdf /> {t.rpt.generateBtn}</>}
          </button>

          {simReportLoading && (
            <div className="sim-generating">
              <div className="ai-loading-bar"><div className="ai-loading-fill" /></div>
              <div className="sim-gen-steps">
                <span className="sim-gen-step active">{t.rpt.stepAnalysis}</span>
                <span className="sim-gen-step">{t.rpt.stepSimulation}</span>
                <span className="sim-gen-step">{t.rpt.stepWriting}</span>
                <span className="sim-gen-step">{t.rpt.stepReview}</span>
              </div>
            </div>
          )}

          {simReport && (
            <div className="sim-report-output">
              <div className="sim-report-header">
                <span>{t.rpt.genReport}</span>
              </div>
              <div className="sim-report-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                  table: ({node, ...props}) => <table className="report-table" {...props} />,
                  th: ({node, ...props}) => <th {...props} />,
                  td: ({node, ...props}) => <td {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="sim-report-callout" {...props} />,
                }}>
                  {simReport}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
